import * as React from 'react';
import '@styles/app.css';
import { switchAccount } from '../auth/msal';
import { http } from '../api/http';
import { FaSignOutAlt, FaSearchPlus } from 'react-icons/fa';

export interface MenuProps {
  selectedDrawerName: string | null;
  onDrawerSelect: (id: number, name: string) => void;
  onRouteChange?: (route: string) => void;
  userName?: string | null;
  userId?: string | null;
  isAdmin?: boolean; // new prop
}

const menuItems = [
  { key: 'drawers', label: 'Drawers', dropdown: true },
  { key: 'browse', label: 'Browse' },
  { key: 'search', label: 'Search' },
  {
    key: 'admin',
    label: 'Admin',
    submenu: [
      { key: 'users', label: 'Users' },
      { key: 'groups', label: 'Groups' },
      { key: 'fielddefinitions', label: 'Field Definitions' },
      { key: 'drawers-admin', label: 'Drawers' },
      { key: 'documenttypes', label: 'Document Types' }
    ]
  }
];

const ZOOM_KEY = 'uiZoomPercent';
const ZOOM_OPTIONS = [70,75,80,85,90,95,100];
const DEFAULT_ZOOM = 85;

export const Menu = ({ selectedDrawerName, onDrawerSelect, onRouteChange, userName, isAdmin }: MenuProps) => {
  const [adminOpen, setAdminOpen] = React.useState(false);
  const [drawersOpen, setDrawersOpen] = React.useState(false);
  const [drawers, setDrawers] = React.useState<{ id: number; drawerName: string }[]>([]);
  const [drawersLoading, setDrawersLoading] = React.useState(false);
  const [drawersError, setDrawersError] = React.useState<string | null>(null);

  // Zoom dropdown state
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const [zoomPercent, setZoomPercent] = React.useState<number>(DEFAULT_ZOOM);

  // Close drawers & zoom dropdown when clicking outside
  const drawersRef = React.useRef<HTMLDivElement | null>(null);
  const zoomRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    function handleDocClick(e: MouseEvent) {
      if (drawersOpen && drawersRef.current && !drawersRef.current.contains(e.target as Node)) {
        setDrawersOpen(false);
      }
      if (zoomOpen && zoomRef.current && !zoomRef.current.contains(e.target as Node)) {
        setZoomOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [drawersOpen, zoomOpen]);

  React.useEffect(() => {
    if (drawersOpen && drawers.length === 0 && !drawersLoading) {
      setDrawersLoading(true);
      http<any[]>('/api/drawer')
        .then(data => { setDrawers(Array.isArray(data) ? data : []); setDrawersError(null); })
        .catch(e => setDrawersError(e.message))
        .finally(() => setDrawersLoading(false));
    }
  }, [drawersOpen, drawers.length, drawersLoading]);

  const handleSwitchAccount = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    switchAccount();
  }, []);

  const handleDrawerSelect = React.useCallback((id: number, name: string) => {
    onDrawerSelect(id, name);
    setDrawersOpen(false);
  }, [onDrawerSelect]);

  function routeForSubKey(subKey: string): string | null {
    switch (subKey) {
      case 'users': return 'admin-users';
      case 'groups': return 'admin-groups';
      case 'drawers-admin': return 'admin-drawers-admin';
      case 'fielddefinitions': return 'admin-fielddefinitions';
      case 'documenttypes': return 'admin-documenttypes';
      default: return null;
    }
  }

  const visibleMenuItems = isAdmin ? menuItems : menuItems.filter(m => m.key !== 'admin');

  // Zoom handling: load stored value and apply CSS variable
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(ZOOM_KEY);
      const val = stored ? Number(stored) : DEFAULT_ZOOM;
      const normalized = ZOOM_OPTIONS.includes(val) ? val : DEFAULT_ZOOM;
      setZoomPercent(normalized);
      document.documentElement.style.setProperty('--ui-scale', String(normalized / 100));
    } catch (e) {
      // ignore
    }
  }, []);

  const applyZoom = React.useCallback((percent: number) => {
    setZoomPercent(percent);
    try {
      localStorage.setItem(ZOOM_KEY, String(percent));
      document.documentElement.style.setProperty('--ui-scale', String(percent / 100));
    } catch (e) {
      // ignore
    }
    setZoomOpen(false);
  }, []);

  return (
    <header className="app-header">
      <div className="brand">

      </div>
      <nav className="main-nav">
        {visibleMenuItems.map(item => {
          if (item.key === 'drawers') {
            return (
              <div
                key={item.key}
                ref={drawersRef}
                className={`nav-item dropdown ${drawersOpen ? 'open' : ''}`}
              >
                <button
                  type="button"
                  className="nav-link nav-link-button"
                  aria-haspopup="true"
                  aria-expanded={drawersOpen}
                  onClick={() => setDrawersOpen(o => !o)}
                >
                  {item.label}
                </button>
                {drawersOpen && (
                  <div className="dropdown-menu">
                    {drawersLoading && <div className="dropdown-status">Loading...</div>}
                    {drawersError && <div className="dropdown-status error">Error: {drawersError}</div>}
                    {!drawersLoading && !drawersError && drawers.length === 0 && <div className="dropdown-status">No drawers found</div>}
                    {drawers.map(drawer => (
                      <button
                        key={drawer.id}
                        type="button"
                        className="dropdown-item"
                        onClick={() => handleDrawerSelect(drawer.id, drawer.drawerName)}
                      >
                        {drawer.drawerName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          if (item.key === 'search' || item.key === 'browse') {
            return (
              <button
                key={item.key}
                type="button"
                className="nav-link nav-link-clickable"
                onClick={() => { if (onRouteChange) onRouteChange(item.key); }}
              >
                {item.label}
              </button>
            );
          }
          if (item.submenu) {
            return (
              <div
                key={item.key}
                className={`nav-item dropdown ${adminOpen ? 'open' : ''}`}
                tabIndex={0}
                onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setAdminOpen(false); }}
              >
                <button
                  type="button"
                  className="nav-link nav-link-button"
                  aria-haspopup="true"
                  aria-expanded={adminOpen}
                  onClick={() => setAdminOpen(o => !o)}
                >
                  {item.label}
                </button>
                {adminOpen && (
                  <div className="dropdown-menu">
                    {item.submenu.map(sub => (
                      <button
                        key={sub.key}
                        type="button"
                        className="dropdown-item"
                        onClick={() => { if (onRouteChange) { const r = routeForSubKey(sub.key); if (r) onRouteChange(r); } setAdminOpen(false); }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <span key={item.key} className="nav-link nav-link-static">{item.label}</span>
          );
        })}
      </nav>
      <div className="drawer-badge-wrapper">
        {/* Zoom control */}
        <div ref={zoomRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="badge-button"
            onClick={() => setZoomOpen(o => !o)}
            title="Zoom"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <FaSearchPlus /> {zoomPercent}%
          </button>
          {zoomOpen && (
            <div className="dropdown-menu" style={{ right: 0, left: 'auto' }}>
              {ZOOM_OPTIONS.map(p => (
                <button key={p} type="button" className="dropdown-item" onClick={() => applyZoom(p)}>
                  {p}%
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedDrawerName && <span className="drawer-badge">Drawer: {selectedDrawerName}</span>}
        {userName && (
          <button
            type="button"
            className="badge-button"
            onClick={handleSwitchAccount}
            title="Switch account"
          >
            <FaSignOutAlt /> {userName}
          </button>
        )}
      </div>
    </header>
  );
}

export default Menu;
